import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../Common/Loading";
import {
  analyzeAllBracketsAndDrawChart,
  analyzeWeeklyPracticeRequired,
  analyzeTimeImprovementMatrix,
  analyzeWLPCompletion,
} from "./Mdanalysistools";
/**
 * React/TS port of EL_MDOverallAnalysis.aspx + EL_MDOverallAnalysis.aspx.vb.
 *
 * Parity notes:
 * - School dropdown: "--Select--" (empty value) + AA / DLR_KE_AU / ISMARTSTUD
 *   / TWIZ, sourced from the same 12-month DISTINCT SchoolCode query,
 *   restricted to those four codes. Selecting "--Select--" clears the table
 *   with no query run — mirrors btngo_Click's early-exit branch. Changing
 *   the dropdown re-runs the report automatically (ddlschools_SelectedIndexChanged
 *   just calls btngo_Click).
 * - Study Year (1-10, default 1), Max Skip % (--All--/10/20/30/40/50,
 *   default 10%), Previous MD / Latest MD checkboxes (both checked by
 *   default), From/To date range (default: last 12 months), Minimum Gap
 *   Days (default 42) — all sent straight through to the report endpoint.
 * - GO button re-runs the report with the current filters.
 * - Table columns and sortability match the VB Repeater + jQuery sortTable:
 *   sortable columns are Study Year, Previous Date, Latest Date, Previous
 *   Time, Latest Time, Progress %, No of WLP, Days Gap, Practice Time.
 *   Progress % defaults to a descending sort on load, same as the original
 *   `sortTable('progress', 'desc')` in $(document).ready.
 * - Progress % cell shows ▲/▼ + the absolute value, colored green/red for
 *   positive/negative, same as the VB inline style + Eval expression.
 * - Row click opens the per-student report in a new window, same size and
 *   URL shape as OpenStudentReport(schoolcode, familycode, login).
 * - Export Excel reproduces ExportTableToExcel(): clones the table, strips
 *   any hidden/no-export cells, bolds + shades the header row, and downloads
 *   an .xls-flavored HTML blob — done here directly against the live table
 *   markup rather than a clone, since the rendered table has no hidden
 *   helper columns to strip.
 * - Close clears nothing session-specific (the VB handler didn't either) and
 *   navigates back to the SysAdmin page.
 * - "View Chart" / "Improvement" / "Time vs Improvement Matrix" / "WLP
 *   Completion Summary" are ported from Script/MDgraph.js in
 *   ./mdAnalysisTools.ts — same bracket definitions, filters (test gap
 *   42-365 days, previous score 20-85%, improvement >= 0, practice time > 0),
 *   median math, and Chart.js rendering, but reading from the `sortedRows`
 *   array instead of scraping table DOM text. They operate as an imperative
 *   overlay (matching the original's floating containers/canvas/modal),
 *   not a React-managed modal, to keep the port 1:1.
 *
 * NOT ported: Script/Studentperformance.js wasn't provided, so anything it
 * drove (if anything on this page) isn't covered here.
 */

interface ReportRow {
  index: number;
  schoolCode: string;
  schoolName: string;
  familyCode: string;
  login: string;
  fname: string;
  studyYearProgress: string;
  previousDate: string; // "dd Mon yyyy HH:mm" (SQL style 105 + 108)
  latestDate: string;
  previousTotalTime: string; // "HH:MM:SS"
  latestTotalTime: string;
  avgPreviousScore: number | null;
  avgLatestScore: number | null;
  avgScoreDiff: number;
  noOfWLP: string; // "actual/expected"
  daysGap: number;
  expectedPractice: string; // "HH:MM:00"
  totalTimeFormatted: string; // "HH:MM:SS"
  previousSkipText: string; // "skipped/total"
  latestSkipText: string;
}

type SortColumn =
  | "studyyear"
  | "previousdate"
  | "latestdate"
  | "previousmdtime"
  | "latestmdtime"
  | "progress"
  | "noofwlp"
  | "daysgap"
  | "totaltime";

type SortOrder = "asc" | "desc";

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

const STUDY_YEAR_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const SKIP_PERCENT_OPTIONS = [
  { label: "--All--", value: "" },
  { label: "10%", value: "10" },
  { label: "20%", value: "20" },
  { label: "30%", value: "30" },
  { label: "40%", value: "40" },
  { label: "50%", value: "50" },
];

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultToDate(): string {
  return toDateInputValue(new Date());
}

function defaultFromDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 12);
  return toDateInputValue(d);
}

// Mirrors parseCustomDate(dateText): "dd-Mon-yyyy HH:mm" style text.
function parseReportDate(text: string): number {
  if (!text) return 0;
  const parts = text.split(" ");
  if (parts.length < 2) return 0;
  const parsed = new Date(`${parts[0]} ${parts[1]}`);
  const t = parsed.getTime();
  return isNaN(t) ? 0 : t;
}

// Mirrors convertTimeToSeconds("HH:MM:SS").
function timeToSeconds(text: string): number {
  if (!text) return 0;
  const parts = text.split(":");
  if (parts.length !== 3) return 0;
  const [h, m, s] = parts.map((p) => parseInt(p, 10) || 0);
  return h * 3600 + m * 60 + s;
}

// Mirrors getCurrentStudyYear(text): last number found in the string.
function lastStudyYear(text: string): number {
  const matches = text.match(/\d+/g);
  if (!matches) return 0;
  return parseInt(matches[matches.length - 1], 10);
}

// Mirrors getPercent("completed/total") used for No of WLP sorting.
function wlpCompletionPercent(text: string): number {
  const parts = text.split("/");
  if (parts.length !== 2) return 0;
  const completed = parseFloat(parts[0]) || 0;
  const total = parseFloat(parts[1]) || 0;
  return total > 0 ? (completed / total) * 100 : 0;
}

const MDOverallAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement>(null);

  const [schools, setSchools] = useState<string[]>([]);
  const [schoolCode, setSchoolCode] = useState<string>("");
  const [studyYear, setStudyYear] = useState<string>("1");
  const [skipPercent, setSkipPercent] = useState<string>("10");
  const [previousMD, setPreviousMD] = useState<boolean>(true);
  const [latestMD, setLatestMD] = useState<boolean>(true);
  const [fromDate, setFromDate] = useState<string>(defaultFromDate());
  const [toDate, setToDate] = useState<string>(defaultToDate());
  const [minGapDays, setMinGapDays] = useState<string>("42");

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  const [sortColumn, setSortColumn] = useState<SortColumn>("progress");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");


  useEffect(() => { 
    document.body.style.backgroundColor = "#fff";
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}api/reports/md-overall-analysis/schools/`, {
          credentials: "include",
        });
        const data: { schools: string[] } = await resp.json();
        setSchools(data.schools);
      } catch {
        setSchools([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const runReport = useCallback(
    async (
      sc: string,
      sy: string,
      sp: string,
      prevMD: boolean,
      latMD: boolean,
      from: string,
      to: string,
      gapDays: string
    ) => {
      if (sc === "") {
        setRows([]);
        setSummary(null);
        setError("");
        setHasSearched(true);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          school_code: sc,
          study_year: sy,
          skip_percent: sp,
          previous_md: String(prevMD),
          latest_md: String(latMD),
          from_date: from,
          to_date: to,
          min_gap_days: gapDays,
        });
        const resp = await fetch(`${API_BASE}api/reports/md-overall-analysis/?${params.toString()}`, {
          credentials: "include",
        });
        if (!resp.ok) {
          throw new Error(`Request failed with status ${resp.status}`);
        }
        const data: { rows: ReportRow[]; summary: string | null } = await resp.json();
        setRows(data.rows);
        setSummary(data.summary);
        setSortColumn("progress");
        setSortOrder("desc");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load report");
        setRows([]);
        setSummary(null);
      } finally {
        setLoading(false);
        setHasSearched(true);
      }
    },
    []
  );

  const handleSchoolChange = (value: string) => {
    setSchoolCode(value);
    runReport(value, studyYear, skipPercent, previousMD, latestMD, fromDate, toDate, minGapDays);
  };

  const handleGoClick = () => {
    runReport(schoolCode, studyYear, skipPercent, previousMD, latestMD, fromDate, toDate, minGapDays);
  };

  const handleSort = (column: SortColumn) => {
    setSortOrder((prevOrder) => (sortColumn === column && prevOrder === "desc" ? "asc" : "desc"));
    setSortColumn(column);
  };

  const sortedRows = useMemo(() => {
    const order = sortOrder === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      let valA = 0;
      let valB = 0;

      switch (sortColumn) {
        case "progress":
          valA = a.avgScoreDiff;
          valB = b.avgScoreDiff;
          break;
        case "noofwlp":
          valA = wlpCompletionPercent(a.noOfWLP);
          valB = wlpCompletionPercent(b.noOfWLP);
          break;
        case "studyyear":
          valA = lastStudyYear(a.studyYearProgress);
          valB = lastStudyYear(b.studyYearProgress);
          break;
        case "previousdate":
          valA = parseReportDate(a.previousDate);
          valB = parseReportDate(b.previousDate);
          break;
        case "latestdate":
          valA = parseReportDate(a.latestDate);
          valB = parseReportDate(b.latestDate);
          break;
        case "previousmdtime":
          valA = timeToSeconds(a.previousTotalTime);
          valB = timeToSeconds(b.previousTotalTime);
          break;
        case "latestmdtime":
          valA = timeToSeconds(a.latestTotalTime);
          valB = timeToSeconds(b.latestTotalTime);
          break;
        case "daysgap":
          valA = a.daysGap;
          valB = b.daysGap;
          break;
        case "totaltime":
          valA = timeToSeconds(a.totalTimeFormatted);
          valB = timeToSeconds(b.totalTimeFormatted);
          break;
      }

      return order * (valA - valB);
    });
  }, [rows, sortColumn, sortOrder]);

  const handleRowClick = (row: ReportRow) => {
    const url =
      `EL_MDAnalysisreport?SchoolCode=${encodeURIComponent(row.schoolCode)}` +
      `&familycode=${encodeURIComponent(row.familyCode)}` +
      `&Login=${encodeURIComponent(row.login)}`;
    window.open(url, "_blank", "toolbar=no,scrollbars=yes,resizable=yes,width=1550,height=700");
  };

  // Mirrors ExportTableToExcel(): bold+shaded header, bordered cells, .xls blob.
  const handleExport = () => {
    const table = tableRef.current;
    if (!table) return;

    setExporting(true);
    try {
      const clone = table.cloneNode(true) as HTMLTableElement;
      const clonedRows = Array.from(clone.rows);

      clonedRows.forEach((row, r) => {
        Array.from(row.cells).forEach((cell) => {
          cell.style.border = "1px solid black";
          cell.style.padding = "5px";
          cell.style.whiteSpace = "nowrap";
          if (r > 0 && cell.textContent?.trim()) {
            cell.setAttribute("style", `${cell.getAttribute("style")};mso-number-format:'\\@';`);
          }
        });
      });

      if (clonedRows.length > 0) {
        Array.from(clonedRows[0].cells).forEach((cell) => {
          cell.style.fontWeight = "bold";
          cell.style.backgroundColor = "#D9E2F3";
          cell.style.border = "1px solid black";
          cell.style.padding = "5px";
        });
      }

      const html =
        '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
        'xmlns:x="urn:schemas-microsoft-com:office:excel" ' +
        'xmlns="http://www.w3.org/TR/REC-html40">' +
        "<head><meta charset=\"UTF-8\"></head>" +
        `<body>${clone.outerHTML}</body></html>`;

      const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "MDOverallAnalysis.xls";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setExporting(false);
    }
  };

  const handleViewChart = () => analyzeAllBracketsAndDrawChart(sortedRows);
  const handleImprovement = () => analyzeWeeklyPracticeRequired(sortedRows);
  const handleTimeImprovementMatrix = () => analyzeTimeImprovementMatrix(sortedRows);
  const handleWLPCompletion = () => analyzeWLPCompletion(sortedRows, studyYear);

  const handleClose = () => {
    navigate("/SysAdmin");
  };

  const sortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return "↕";
    return sortOrder === "asc" ? "▲" : "▼";
  };

  const showTable = !loading && !error && hasSearched;
  const showNoRecords = showTable && rows.length === 0;

  return (
    <div className="mda-report">
      {/* ===== TITLE ===== */}
      <div className="mda-title-bar">MD OVERALL ANALYSIS</div>

      {/* ===== FILTER ROW ===== */}
      <div className="mda-filter-row">
        <div className="mda-filter-col mda-filter-col-main">
          <div className="mda-section-label">🔽 FILTERS</div>
          <div className="mda-filter-inline">
            <label>School Code:</label>
            <select value={schoolCode} onChange={(e) => handleSchoolChange(e.target.value)}>
              <option value="">--Select--</option>
              {schools.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>

            <label>Study Year:</label>
            <select value={studyYear} onChange={(e) => setStudyYear(e.target.value)}>
              {STUDY_YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <label>Max Skip %:</label>
            <select value={skipPercent} onChange={(e) => setSkipPercent(e.target.value)}>
              {SKIP_PERCENT_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <label className="mda-checkbox-label">
              <input
                type="checkbox"
                checked={previousMD}
                onChange={(e) => setPreviousMD(e.target.checked)}
              />
              Previous MD
            </label>
            <label className="mda-checkbox-label">
              <input type="checkbox" checked={latestMD} onChange={(e) => setLatestMD(e.target.checked)} />
              Latest MD
            </label>

            <label>From:</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />

            <label>TO:</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />

            <label>Minimum Gap Days:</label>
            <input
              type="text"
              className="mda-gap-input"
              value={minGapDays}
              onChange={(e) => setMinGapDays(e.target.value)}
            />

            <button type="button" className="mda-btn mda-btn-go" style={{backgroundColor:"#efefef", color:"black", border:"1px solid #888888"}} onClick={handleGoClick}>
              GO
            </button>
          </div>
        </div>

        <div className="mda-filter-col mda-filter-col-view">
          <div className="mda-section-label">👁 VIEW</div>
          <div className="mda-view-buttons">
            <button type="button" className="mda-btn" onClick={handleViewChart} disabled={rows.length === 0}>
              View Chart
            </button>
            <button
              type="button"
              className="mda-btn"
              onClick={handleImprovement}
              disabled={rows.length === 0}
            >
              Improvement
            </button>
            <button
              type="button"
              className="mda-btn"
              onClick={handleTimeImprovementMatrix}
              disabled={rows.length === 0}
            >
              Time vs Improvement Matrix
            </button>
            <button
              type="button"
              className="mda-btn"
              onClick={handleWLPCompletion}
              disabled={rows.length === 0}
            >
              WLP Completion Summary
            </button>
          </div>
        </div>

        <div className="mda-filter-col mda-filter-col-actions">
          <div className="mda-section-label">⚡ ACTIONS</div>
          <div className="mda-action-buttons">
            <button
              type="button"
              className="mda-btn"
              onClick={handleExport}
              disabled={exporting || rows.length === 0}
            >
              {exporting ? "Preparing Excel..." : "Export Excel"}
            </button>
            <button type="button" className="mda-btn mda-btn-close" onClick={handleClose}>
              CLOSE
            </button>
          </div>
        </div>
      </div>

      {summary && <div className="mda-summary" dangerouslySetInnerHTML={{ __html: summary }} />}

      {/* ===== TABLE ===== */}
      <div className="mda-table-wrapper">
        {loading && (
          <div className="mda-loading-wrap">
            <Loading />
          </div>
        )}

        {!loading && error && <div className="mda-alert mda-alert-danger">{error}</div>}

        {showTable && rows.length > 0 && (
          <table id="tblLessons" ref={tableRef} className="lesson-table">
            <thead>
              <tr className="lesson-header">
                <th>#</th>
                <th>Dealer</th>
                <th>Login</th>
                <th>Child</th>
                <th className="mda-sortable" onClick={() => handleSort("studyyear")}>
                  Study Year <span className="mda-sort-icon">{sortIcon("studyyear")}</span>
                </th>
                <th className="mda-sortable" onClick={() => handleSort("previousdate")}>
                  Previous Date <span className="mda-sort-icon">{sortIcon("previousdate")}</span>
                </th>
                <th className="mda-sortable" onClick={() => handleSort("latestdate")}>
                  Latest Date <span className="mda-sort-icon">{sortIcon("latestdate")}</span>
                </th>
                <th className="mda-sortable" onClick={() => handleSort("previousmdtime")}>
                  Previous Time <span className="mda-sort-icon">{sortIcon("previousmdtime")}</span>
                </th>
                <th className="mda-sortable" onClick={() => handleSort("latestmdtime")}>
                  Latest Time <span className="mda-sort-icon">{sortIcon("latestmdtime")}</span>
                </th>
                <th>Previous Avg %</th>
                <th>Latest Avg %</th>
                <th className="mda-sortable" onClick={() => handleSort("progress")}>
                  Progress % <span className="mda-sort-icon">{sortIcon("progress")}</span>
                </th>
                <th className="mda-sortable" onClick={() => handleSort("noofwlp")}>
                  No of WLP <span className="mda-sort-icon">{sortIcon("noofwlp")}</span>
                </th>
                <th className="mda-sortable" onClick={() => handleSort("daysgap")}>
                  Days Gap <span className="mda-sort-icon">{sortIcon("daysgap")}</span>
                </th>
                <th>Expected Practice</th>
                <th className="mda-sortable" onClick={() => handleSort("totaltime")}>
                  Practice Time <span className="mda-sort-icon">{sortIcon("totaltime")}</span>
                </th>
                <th>Previous skip</th>
                <th>Latest skip</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, i) => {
                const positive = row.avgScoreDiff >= 0;
                return (
                  <tr
                    key={`${row.schoolCode}-${row.familyCode}-${row.login}`}
                    className="lesson-row"
                    onClick={() => handleRowClick(row)}
                  >
                    <td>{i + 1}</td>
                    <td>{row.schoolName}</td>
                    <td>{row.familyCode}</td>
                    <td>{row.fname}</td>
                    <td>{row.studyYearProgress}</td>
                    <td>{row.previousDate}</td>
                    <td>{row.latestDate}</td>
                    <td>{row.previousTotalTime}</td>
                    <td>{row.latestTotalTime}</td>
                    <td>{row.avgPreviousScore ?? ""}</td>
                    <td>{row.avgLatestScore ?? ""}</td>
                    <td style={{ color: positive ? "green" : "red", fontWeight: "bold" }}>
                      {positive ? "▲ " : "▼ "}
                      {Math.abs(Math.round(row.avgScoreDiff * 100) / 100)}
                    </td>
                    <td>{row.noOfWLP}</td>
                    <td>{row.daysGap}</td>
                    <td>{row.expectedPractice}</td>
                    <td>{row.totalTimeFormatted}</td>
                    <td>{row.previousSkipText}</td>
                    <td>{row.latestSkipText}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {showNoRecords && <div className="mda-alert mda-alert-warning">No records found</div>}
      </div>

      {/* ===== IN-FILE CSS — no Bootstrap or any external stylesheet required ===== */}
      <style>{`
        .mda-report {
          font-family: Arial, Helvetica, sans-serif;
          padding: 8px 14px;
          box-sizing: border-box;
        }
        .mda-report *, .mda-report *::before, .mda-report *::after {
          box-sizing: border-box;
        }

        .mda-title-bar {
          text-align: center;
          font-size: 18px;
          font-weight: bold;
          color: #fff;
          background-color: teal;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 10px;
        }

        .mda-filter-row {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          padding: 10px 0;
          border-bottom: 1px solid #d9d9d9;
          margin-bottom: 10px;
        }
        .mda-filter-col {
          padding-right: 15px;
          border-right: 1px solid #d9d9d9;
        }
        .mda-filter-col:last-child {
          border-right: none;
        }
        .mda-filter-col-main {
          flex: 2 1 480px;
        }
        .mda-filter-col-view {
          flex: 1 1 260px;
        }
        .mda-filter-col-actions {
          flex: 0 0 180px;
        }

        .mda-section-label {
          font-size: 15px;
          font-weight: bold;
          color: #0b6a78;
          margin-bottom: 10px;
        }

        .mda-filter-inline {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .mda-filter-inline label {
          font-size: 14px;
          font-weight: bold;
          color: #1f3a6d;
          margin-right: 4px;
        }
        .mda-filter-inline select,
        .mda-filter-inline input[type="date"],
        .mda-filter-inline input[type="text"] {
          height: 27px;
          font-family: Arial;
          font-size: 13px;
          padding: 2px 6px;
          border: 1px solid #ced4da;
          border-radius: 4px;
        }
        .mda-gap-input {
          width: 50px;
        }
        .mda-checkbox-label {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .mda-view-buttons, .mda-action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .mda-action-buttons {
          flex-direction: column;
        }

        .mda-btn {
          height: 30px;
          padding: 0 12px;
          font-family: Arial;
          font-size: 13px;
          border: 1px solid #888888;
          border-radius: 4px;
          background: #efefef;
          cursor: pointer;
        }
        .mda-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .mda-btn-go {
          background: #0d6efd;
          border-color: #0d6efd;
          color: #fff;
          font-size: 14px;
        }
        .mda-btn-success {
          background: #198754;
          border-color: #198754;
          color: #fff;
        }
        .mda-btn-info {
          background: #0dcaf0;
          border-color: #0dcaf0;
          color: #062c33;
        }
        .mda-btn-close {
          background: #efefef;
        }

        .mda-summary {
          background: #eef3ff;
          border: 1px solid #5c84c3;
          padding: 10px;
          margin-bottom: 10px;
          font-size: 14px;
        }

        .mda-table-wrapper {
          width: 100%;
          overflow-x: auto;
        }

        .lesson-table {
          border-collapse: collapse;
          table-layout: auto;
        }
        .lesson-header th {
          background-color: #b8cce4;
          color: #1f3a6d;
          border: 1px solid #5c84c3;
          padding: 5px;
          font-weight: bold;
          height: 25px;
          text-align: left;
          white-space: nowrap;
        }
        .mda-sortable {
          cursor: pointer;
          user-select: none;
        }
        .mda-sort-icon {
          display: inline-block;
          margin-left: 4px;
          font-size: 10px;
          color: #0b3d91;
        }
        .lesson-row td {
          border: 1px solid #5c84c3;
          padding: 5px;
          background-color: #edf2fb;
          height: 25px;
          font-size: 14px;
          white-space: nowrap;
        }
        .lesson-row:hover td {
          background-color: #9dc3e6;
          cursor: pointer;
        }

        .mda-loading-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 0;
        }

        .mda-alert {
          margin: 12px 0;
          padding: 10px 16px;
          border-radius: 8px;
          text-align: center;
          font-size: 14px;
        }
        .mda-alert-danger {
          background: #f8d7da;
          color: #842029;
          border: 1px solid #f5c2c7;
        }
        .mda-alert-warning {
          background: #fff3cd;
          color: #664d03;
          border: 1px solid #ffecb5;
        }
      `}</style>
    </div>
  );
};

export default MDOverallAnalysis;